import { makeAutoObservable, runInAction } from 'mobx';
import isNil from 'lodash/isNil';

import { sortRepository } from '@/catalog/repository/SortRepository';
import { SCREEN_MD, SCREEN_SM, SCREEN_XS } from '@/common/constants/breakpoints';
import { HeaderSection } from '@/common/constants/HeaderSection';
import { IS_PORTABLE } from '@/common/helpers/mobileDetect';
import useStore from '@/common/hooks/useStore';
import APIDriver from '@/common/services/session/APIDriver';
import LocalStorageDriver from '@/common/services/session/LocalStorageDriver';
import UserSessionDataManager from '@/common/services/session/UserSessionDataManager';
import Storage, { KEY_IS_SIDEBAR_OPENED } from '@/common/services/Storage';
import Sort from '@/common/state/Sort';
import ConfigurationStore, { configurationStore } from '@/common/stores/ConfigurationStore';
import { AppContextProductType, ProductType } from '@/product/constants/productTypes';

export default class AppStore {
  /*
   * In application we call "setProductTypeContext" as soon as possible,
   * so "productTypeContext" is always defined
   * */
  productTypeContext!: AppContextProductType;

  headerSection: Nullable<HeaderSection> = null;

  isSidebarOpened: boolean;

  isRightSidebarOpened = false;

  isHQModeEnabled: boolean;

  contentAreaWidth: Nullable<number> = null;

  screenWidth: Nullable<number> = null;

  screenHeight: Nullable<number> = null;

  orientation: OrientationType = screen.orientation.type;

  readonly configurationStore: ConfigurationStore;

  readonly userSessionDataManager: UserSessionDataManager;

  constructor(configurationStore: ConfigurationStore) {
    makeAutoObservable(this, {
      configurationStore: false,
      userSessionDataManager: false,
      isLandscape: false,
      enableCloudSettingsStorage: false,
    });

    this.configurationStore = configurationStore;

    this.userSessionDataManager = new UserSessionDataManager(new LocalStorageDriver());

    const isSidebarOpened = Storage.getObject(KEY_IS_SIDEBAR_OPENED, Storage.STORAGE_LOCAL);

    this.isSidebarOpened = !isNil(isSidebarOpened) ? isSidebarOpened : !IS_PORTABLE;
    this.isHQModeEnabled = window.devicePixelRatio > 1;
  }

  get defaultSort() {
    const defaultSort = this.activeSortList[0];

    return new Sort().setSingleSort(defaultSort.field, defaultSort.direction);
  }

  get activeSortList() {
    return sortRepository.getSortsByProductType(this.productTypeContext);
  }

  get isDiamondContext() {
    return [
      ProductType.DiamondColorless,
      ProductType.DiamondFancy,
      ProductType.LGDColorless,
      ProductType.LGDFancy,
    ].includes(this.productTypeContext);
  }

  get isRoughContext() {
    return [ProductType.Rough, ProductType.LGDRough].includes(this.productTypeContext);
  }

  get isJewelryContext() {
    return ProductType.Jewelry === this.productTypeContext;
  }

  get isGemContext() {
    return ProductType.Gem === this.productTypeContext;
  }

  get isNaturalContext() {
    return [ProductType.DiamondColorless, ProductType.DiamondFancy, ProductType.Rough].includes(
      this.productTypeContext,
    );
  }

  get isLGDContext() {
    return this.checkLGDContext(this.productTypeContext);
  }

  get isColorlessContext() {
    return this.checkColorlessContext(this.productTypeContext);
  }

  get isLandscape() {
    return window.innerWidth > window.innerHeight;
  }

  get isPhone() {
    return this.isPhonePortrait || this.isPhoneLandscape;
  }

  // `isPhonePortrait === true` means that user rotated mobile device vertically
  get isPhonePortrait() {
    return this.screenWidth && this.screenWidth <= SCREEN_XS && !this.isLandscape;
  }

  // `isPhoneLandscape === true` means that user rotated mobile device horizontally
  get isPhoneLandscape() {
    return this.screenWidth && this.screenWidth <= SCREEN_MD && this.isLandscape;
  }

  get isTabletPortrait() {
    return this.screenWidth && this.screenWidth >= SCREEN_SM && this.screenWidth <= SCREEN_MD && !this.isLandscape;
  }

  get activeB2BList() {
    let b2bFiltersKey = ProductType.Diamond;

    if (!this.isDiamondContext) {
      b2bFiltersKey = this.productTypeContext;
    }

    if (this.isRoughContext) {
      b2bFiltersKey = ProductType.Rough;
    }

    return this.configurationStore.b2bFilters?.[b2bFiltersKey] ?? [];
  }

  checkLGDContext(context: AppContextProductType) {
    return [ProductType.LGDColorless, ProductType.LGDFancy, ProductType.LGDRough].includes(context);
  }

  checkColorlessContext(context: AppContextProductType) {
    return [ProductType.DiamondColorless, ProductType.LGDColorless].includes(context);
  }

  toggleSidebar = () => {
    this.isSidebarOpened = !this.isSidebarOpened;

    Storage.saveObject(KEY_IS_SIDEBAR_OPENED, this.isSidebarOpened, Storage.STORAGE_LOCAL);
  };

  openRightSidebar = () => {
    this.isRightSidebarOpened = true;
  };

  closeRightSidebar = () => {
    this.isRightSidebarOpened = false;
  };

  toggleRightSidebar = () => {
    this.isRightSidebarOpened = !this.isRightSidebarOpened;
  };

  setProductTypeContext = (context: AppContextProductType) => {
    runInAction(() => {
      this.productTypeContext = context;
    });
  };

  setHeaderSection = (headerSection: Nullable<HeaderSection>) => {
    this.headerSection = headerSection;
  };

  setContainerWidth = (width: number) => {
    this.contentAreaWidth = width;
  };

  setScreenWidth = (width: number) => {
    this.screenWidth = width;
  };

  setScreenHeight = (height: number) => {
    this.screenHeight = height;
  };

  setScreenOrientation = (orientation: OrientationType) => {
    this.orientation = orientation;
  };

  enableCloudSettingsStorage = () => {
    this.userSessionDataManager.setDriver(new APIDriver());
  };
}

export const useAppStore = () => useStore(AppStore);

export const appStore = new AppStore(configurationStore);
